import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance";

/* =========================
   DATE FORMATTER
========================= */
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

function WagesCalculator() {
  const navigate = useNavigate();

  /* =========================
     MONTH + YEAR
  ========================= */
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const selectedPeriod = `${year}-${String(month).padStart(2, "0")}`;

  /* =========================
     STATE
  ========================= */
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [kpi, setKpi] = useState({
    totalAmount: 0,
    completedLotAmount: 0,
    wipAmount: 0,
  });

  const [summaryRows, setSummaryRows] = useState([]);
  const [lotRows, setLotRows] = useState([]);
  const [paymentLogicRows, setPaymentLogicRows] = useState([]);

  /* =========================
     PAGINATION
  ========================= */
  const rowsPerPage = 10;
  const [summaryPage, setSummaryPage] = useState(1);
  const [lotPage, setLotPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);

  /* =========================
     FETCH DATA
  ========================= */
  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        kpiRes,
        summaryRes,
        lotRes,
        paymentRes,
      ] = await Promise.all([
        axios.get(`/wages/summary?month=${selectedPeriod}`),
        axios.get(`/wages/monthly?month=${selectedPeriod}`),
        axios.get(`/wages/monthly-lotwise?month=${selectedPeriod}`),
        axios.get(`/wages/payment-logic?month=${selectedPeriod}`),
      ]);

      setKpi(kpiRes.data || {});
      setSummaryRows(summaryRes.data || []);
      setLotRows(lotRes.data || []);
      setPaymentLogicRows(paymentRes.data || []);

      setSummaryPage(1);
      setLotPage(1);
      setPaymentPage(1);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [year, month]);

  /* =========================
     REFRESH (RUN SPs)
  ========================= */
  const refreshWages = async () => {
    try {
      setRefreshing(true);
      await axios.post("/wages/refresh");
      await fetchData();
      alert("Wages data refreshed successfully");
    } catch {
      alert("Failed to refresh wages data");
    } finally {
      setRefreshing(false);
    }
  };

  /* =========================
     EXPORT HELPERS
  ========================= */
  const exportSingle = async (url, filename) => {
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  const exportSummary = () =>
    exportSingle(
      `/wages/export/monthly?month=${selectedPeriod}`,
      `Wages_Summary_${selectedPeriod}.xlsx`
    );

  const exportLotWise = () =>
    exportSingle(
      `/wages/export/monthly-lotwise?month=${selectedPeriod}`,
      `Wages_LotWise_${selectedPeriod}.xlsx`
    );

  const exportPaymentMandate = () =>
    exportSingle(
      `/wages/export/payment-mandate?month=${selectedPeriod}`,
      `Wages_Payment_Mandate_${selectedPeriod}.xlsx`
    );

  /* =========================
     HELPERS
  ========================= */
  const paginate = (rows, page) =>
    rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const summaryCols = useMemo(
    () => (summaryRows[0] ? Object.keys(summaryRows[0]) : []),
    [summaryRows]
  );
  const lotCols = useMemo(
    () => (lotRows[0] ? Object.keys(lotRows[0]) : []),
    [lotRows]
  );
  const paymentCols = useMemo(
    () => (paymentLogicRows[0] ? Object.keys(paymentLogicRows[0]) : []),
    [paymentLogicRows]
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const exportPayroll = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From & To Date");
      return;
    }

    try {
      const response = await axios.get(
        `/payroll/export?fromDate=${fromDate}&toDate=${toDate}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Payroll_${fromDate}_to_${toDate}.xlsx`;
      link.click();
    } catch {
      alert("Export failed");
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="p-4">
      {/* TOP BAR */}
      <div className="flex justify-between items-center mb-4">

        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1 bg-gray-700 text-white rounded text-sm"
        >
          ⬅ Back
        </button>

        <div className="flex items-center gap-3 bg-white border rounded px-4 py-2 shadow">
          <button onClick={() => setYear(y => y - 1)}>◀</button>
          <span className="font-semibold">{year}</span>
          <button onClick={() => setYear(y => y + 1)}>▶</button>

          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            {[
              "January","February","March","April","May","June",
              "July","August","September","October","November","December",
            ].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>

          <button
            onClick={refreshWages}
            disabled={refreshing}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>

          <button
            onClick={exportPaymentMandate}
            className="px-3 py-1 bg-green-700 text-white rounded text-sm"
          >
            Export Payment + Mandate
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Total Amount" value={kpi.totalAmount} />
        <Kpi label="Completed Lot Amount" value={kpi.completedLotAmount} />
        <Kpi label="WIP Amount" value={kpi.wipAmount} />
      </div>

      <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-gray-100">

        {/* Section Heading */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Payroll Export Filter
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* From Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          {/* To Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
            />
          </div>

          {/* Button */}
          <div className="flex items-end">
            <button
              onClick={exportPayroll}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow hover:shadow-lg transition-all duration-300"
            >
              Export Payroll
            </button>
          </div>

        </div>
      </div>

      {/* SUMMARY TABLE */}
      <Table
        title="Worker Wages Monthly (Summary)"
        columns={summaryCols}
        rows={paginate(summaryRows, summaryPage)}
        page={summaryPage}
        setPage={setSummaryPage}
        total={summaryRows.length}
        loading={loading}
        rightAction={
          <button onClick={exportSummary} className="px-3 py-1 bg-green-600 text-white rounded text-sm">
            Export Excel
          </button>
        }
      />

      {/* LOT-WISE TABLE */}
      <Table
        title="Worker Wages Monthly (Lot Wise)"
        columns={lotCols}
        rows={paginate(lotRows, lotPage)}
        page={lotPage}
        setPage={setLotPage}
        total={lotRows.length}
        loading={loading}
        rightAction={
          <button onClick={exportLotWise} className="px-3 py-1 bg-green-600 text-white rounded text-sm">
            Export Excel
          </button>
        }
      />

      {/* PAYMENT LOGIC TABLE */}
      <Table
        title="Wages Payment Logic"
        columns={paymentCols}
        rows={paginate(paymentLogicRows, paymentPage)}
        page={paymentPage}
        setPage={setPaymentPage}
        total={paymentLogicRows.length}
        loading={loading}
      />
    </div>
  );
}

/* =========================
   REUSABLE COMPONENTS
========================= */
const Kpi = ({ label, value }) => (
  <div className="bg-white border rounded shadow p-4">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-xl font-semibold">
      ₹ {Number(value || 0).toLocaleString("en-IN")}
    </div>
  </div>
);

const Table = ({
  title,
  columns,
  rows,
  loading,
  page,
  setPage,
  total,
  rightAction,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-semibold">{title}</h2>
        {rightAction}
      </div>

      <div className="border rounded overflow-auto">
        {loading ? (
          <div className="p-4 text-gray-500">Loading…</div>
        ) : (
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="border px-2 py-1 text-left">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center p-3">
                    No data
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c} className="border px-2 py-1">
                        {["date","period","created"].some(k =>
                          c.toLowerCase().includes(k)
                        )
                          ? formatDate(r[c])
                          : String(r[c] ?? "")
                        }
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-2 flex justify-between text-sm">
        <span>Page {page} of {totalPages}</span>
        <div className="space-x-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default WagesCalculator;
